import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'

// Thrown by HMRC-dependent actions when the API returns 498 (the HMRC access/refresh
// tokens have both expired). Callers should catch this specifically and prompt the user
// to re-run the HMRC OAuth setup flow (see getHmrcLoginUrl) rather than treat it as a
// generic error.
export class HmrcTokenExpiredError extends Error {
  constructor() {
    super('HMRC tokens have expired. The user must re-authenticate with HMRC.')
    this.name = 'HmrcTokenExpiredError'
  }
}

const HMRC_TOKEN_EXPIRED_STATUS = 498

const HMRC_NOT_FOUND_STATUS = 404

const getErrorStatus = (error) => error?.status || error?.response?.status || error?.cause?.status

// Only the endpoints that actually call out to HMRC can return 498 (see the ITSA
// frontend guide, section 3) — everything else propagates its real error untouched.
const rejectHmrcDependentCall = (error) => {
  if (getErrorStatus(error) === HMRC_TOKEN_EXPIRED_STATUS) throw new HmrcTokenExpiredError()
  throw error
}

const initialState = {
  hmrcBaseUrl: null,
  hmrcLoginUrl: null,
  hmrcAccessTokens: [],
  hasHmrcAccessToken: false,
  businesses: [],
  businessExtended: null,
  businessObligations: [],
  calculatedCumulativePeriodSummary: null,
  hmrcCategories: [],
  categoryMappings: [],
  submissionHistory: [],
  accounts: []
}

export const useHMRCITSAStore = storeWrapper('useHMRCITSAStore', (set) => ({
  ...initialState,

  // ── OAuth setup ──────────────────────────────────────────────────────────────

  getHmrcBaseUrl: async () => {
    const hmrcBaseUrl = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/GetBaseUrl'
    })

    set({ hmrcBaseUrl })
    return hmrcBaseUrl
  },

  getHmrcLoginUrl: async ({ nationalInsuranceNumber }) => {
    const hmrcLoginUrl = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/GetLoginUrl',
      data: { nationalInsuranceNumber }
    })

    set({ hmrcLoginUrl })
    return hmrcLoginUrl
  },

  // The OAuth callback — HMRC calls this directly, not the frontend. Kept here for
  // completeness/testability only.
  getHmrcToken: async ({ code, state, error, errorDescription }) => {
    return ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/GetToken',
      data: { code, state, error, error_description: errorDescription }
    })
  },

  // Named distinctly from the `hasHmrcAccessToken` state field it caches — reusing the
  // same key here would let a later `reset()` (which merges initialState back in)
  // clobber this very function with the boolean.
  checkHasHmrcAccessToken: async ({ nationalInsuranceNumber }) => {
    const hasHmrcAccessToken = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/HasHmrcAccessTokenByNationalInsuranceNumber',
      data: { nationalInsuranceNumber }
    })

    set({ hasHmrcAccessToken: !!hasHmrcAccessToken })
    return !!hasHmrcAccessToken
  },

  getAllHmrcAccessTokens: async () => {
    const hmrcAccessTokens = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcAccessToken/GetAll'
    })

    set({ hmrcAccessTokens })
    return hmrcAccessTokens
  },

  // ── Businesses + obligations (HMRC-dependent — can 498) ───────────────────────

  getBusinessesByNationalInsuranceNumber: async ({ nationalInsuranceNumber }) => {
    try {
      const businesses = await ospreyAPI({
        method: 'GET',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/GetBusinessesByNationalInsuranceNumber',
        data: { nationalInsuranceNumber }
      })

      set({ businesses })
      return businesses
    } catch (error) {
      rejectHmrcDependentCall(error)
    }
  },

  getBusinessExtended: async ({ nationalInsuranceNumber, businessId }) => {
    try {
      const businessExtended = await ospreyAPI({
        method: 'GET',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/GetBusinessExtendedByNationalInsuranceNumberAndBusinessId',
        data: { nationalInsuranceNumber, businessId }
      })

      set({ businessExtended })
      return businessExtended
    } catch (error) {
      rejectHmrcDependentCall(error)
    }
  },

  getSelfEmploymentBusinessObligations: async ({ nationalInsuranceNumber, obligationStatus, businessId }) => {
    try {
      const businessObligations = await ospreyAPI({
        method: 'GET',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/GetSelfEmploymentBusinessObligations',
        data: { nationalInsuranceNumber, obligationStatus, businessId }
      })

      set({ businessObligations })
      return businessObligations
    } catch (error) {
      rejectHmrcDependentCall(error)
    }
  },

  // ── Calculate (ledger only — no HMRC call, cannot 498) ────────────────────────

  calculateSelfEmploymentCumulativePeriodSummary: async ({
    nationalInsuranceNumber,
    businessId,
    periodStartDate,
    periodEndDate,
    accountingBasis
  }) => {
    const calculatedCumulativePeriodSummary = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/CalculateSelfEmploymentCumulativePeriodSummary',
      data: { nationalInsuranceNumber, businessId, periodStartDate, periodEndDate, accountingBasis }
    })

    set({ calculatedCumulativePeriodSummary })
    return calculatedCumulativePeriodSummary
  },

  // ── Submit (HMRC-dependent — can 498) ──────────────────────────────────────────
  // `request` is the full CreateCumulativePeriodSummaryRequest / CreateAnnualSummaryRequest
  // object (camelCase, matching the ITSA frontend guide types exactly) — sent as a plain
  // JSON body, never multipart/form-data.

  createSelfEmploymentCumulativePeriodSummary: async (request) => {
    try {
      return await ospreyAPI({
        method: 'PUT',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/CreateSelfEmploymentCumulativePeriodSummary',
        data: request
      })
    } catch (error) {
      rejectHmrcDependentCall(error)
    }
  },

  createSelfEmploymentAnnualSummary: async (request) => {
    try {
      return await ospreyAPI({
        method: 'PUT',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/CreateSelfEmploymentAnnualSummary',
        data: request
      })
    } catch (error) {
      rejectHmrcDependentCall(error)
    }
  },

  // ── Retrieve / delete (HMRC-dependent — can 498) ──────────────────────────────
  // HMRC production checklist: submit AND retrieve in-year summaries, and the full annual
  // create/retrieve/delete set. A 404 means HMRC holds nothing — resolved as null, not an error.

  retrieveSelfEmploymentCumulativePeriodSummary: async ({ nationalInsuranceNumber, businessId, taxYear }) => {
    try {
      const summary = await ospreyAPI({
        method: 'GET',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/RetrieveSelfEmploymentCumulativePeriodSummary',
        suppress404: true, // 404 = HMRC holds nothing; handled below, no global toast
        data: { nationalInsuranceNumber, businessId, taxYear }
      })
      return summary || null
    } catch (error) {
      if (getErrorStatus(error) === HMRC_NOT_FOUND_STATUS) return null
      rejectHmrcDependentCall(error)
    }
  },

  retrieveSelfEmploymentAnnualSummary: async ({ nationalInsuranceNumber, businessId, taxYear }) => {
    try {
      const summary = await ospreyAPI({
        method: 'GET',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/RetrieveSelfEmploymentAnnualSummary',
        suppress404: true,
        data: { nationalInsuranceNumber, businessId, taxYear }
      })
      return summary || null
    } catch (error) {
      if (getErrorStatus(error) === HMRC_NOT_FOUND_STATUS) return null
      rejectHmrcDependentCall(error)
    }
  },

  // Resolves true on success (204), null when HMRC holds no annual summary (404).
  deleteSelfEmploymentAnnualSummary: async ({ nationalInsuranceNumber, businessId, taxYear }) => {
    try {
      await ospreyAPI({
        method: 'DELETE',
        service: 'accounts',
        endpoint: 'HmrcMtdItsa/DeleteSelfEmploymentAnnualSummary',
        suppress404: true,
        sendAsUrlParams: true,
        data: { nationalInsuranceNumber, businessId, taxYear }
      })
      return true
    } catch (error) {
      if (getErrorStatus(error) === HMRC_NOT_FOUND_STATUS) return null
      rejectHmrcDependentCall(error)
    }
  },

  // ADR 0031 amendment (BE9b, FE8): mints the acknowledgementToken for the annual path —
  // local-only (no HMRC call, cannot 498, unlike the two actions above), mirroring how
  // calculateSelfEmploymentCumulativePeriodSummary is also ledger/local-only. `request` is
  // { nationalInsuranceNumber, businessId, annualSummary } — the EXACT figures that must
  // also be submitted via createSelfEmploymentAnnualSummary, or BE6's figuresHash check
  // rejects the submission.
  prepareAnnualSummaryAcknowledgement: async (request) => {
    return ospreyAPI({
      method: 'POST',
      service: 'accounts',
      endpoint: 'HmrcMtdItsa/PrepareAnnualSummaryAcknowledgement',
      data: request
    })
  },

  // ── Categories + mappings (local DB only, cannot 498) ─────────────────────────

  getHmrcCategories: async ({ isAnnual }) => {
    const hmrcCategories = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategory/GetAllByIsAnnual',
      data: { isAnnual }
    })

    set({ hmrcCategories })
    return hmrcCategories
  },

  getCategoryMappings: async () => {
    const categoryMappings = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategoryMapping/GetAll'
    })

    set({ categoryMappings })
    return categoryMappings
  },

  getCategoryMappingsByCategoryId: async ({ hmrcMtdItsaCategoryId }) => {
    return ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategoryMapping/GetAllByHmrcMtdItsaCategoryId',
      data: { hmrcMtdItsaCategoryId }
    })
  },

  createCategoryMapping: async ({ hmrcMtdItsaCategoryId, sourceType, nominalCode, description }) => {
    return ospreyAPI({
      method: 'POST',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategoryMapping/Create',
      data: { hmrcMtdItsaCategoryId, sourceType, nominalCode, description }
    })
  },

  updateCategoryMapping: async ({ hmrcMtdItsaCategoryMappingId, hmrcMtdItsaCategoryId, sourceType, nominalCode, description }) => {
    return ospreyAPI({
      method: 'PUT',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategoryMapping/Update',
      data: { hmrcMtdItsaCategoryMappingId, hmrcMtdItsaCategoryId, sourceType, nominalCode, description }
    })
  },

  deleteCategoryMapping: async ({ hmrcMtdItsaCategoryMappingId }) => {
    return ospreyAPI({
      method: 'DELETE',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaCategoryMapping/Delete',
      sendAsUrlParams: true,
      data: { hmrcMtdItsaCategoryMappingId }
    })
  },

  // ── Submission history (read-only, cannot 498) ────────────────────────────────

  getSubmissionHistory: async ({ nationalInsuranceNumber }) => {
    const submissionHistory = await ospreyAPI({
      method: 'GET',
      service: 'accounts',
      endpoint: 'HmrcMtdItsaSubmission/GetAllByNationalInsuranceNumber',
      data: { nationalInsuranceNumber }
    })

    set({ submissionHistory })
    return submissionHistory
  },

  // ── Nominal account lookup (feeds the mapping UI's nominalCode select) ───────

  getAllAccountsByType: async ({ accountType }) => {
    const accounts = await ospreyAPI({
      method: 'GET',
      service: 'lookups',
      endpoint: 'Account/GetAllAccountsByType',
      // Lookups AccountController binds [FromQuery] string type (values B|D|J|N|P),
      // not `accountType` — send the single-letter code under the `type` key.
      data: { type: accountType }
    })

    set({ accounts })
    return accounts
  },

  reset: () => set(initialState)
}))
